import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import confetti from "canvas-confetti";
import {
  Baby, Heart, Sparkles, Home, Flame, Trophy,
  ArrowRight, RotateCcw, Star, MessageCircle,
} from "lucide-react";
import {
  GAME_QUESTIONS,
  AVEN_LIFELINE_HINTS,
  CATEGORIES,
  FINAL_LOVE_LETTER,
} from "../../data/valentineGameData";
import "./ValentineGame.css";

/* ── images ── */
const AVEN_IMAGES = [
  process.env.PUBLIC_URL + "/WhatsApp Image 2026-01-23 at 05.46.14.jpeg",
  process.env.PUBLIC_URL + "/WhatsApp Image 2026-02-13 at 11.36.42.jpeg",
  process.env.PUBLIC_URL + "/WhatsApp Image 2026-02-13 at 11.37.14.jpeg",
  process.env.PUBLIC_URL + "/WhatsApp Image 2026-02-13 at 11.38.05.jpeg",
];

const AVEN_HAPPY = process.env.PUBLIC_URL + "/aven happy.jpeg";
const AVEN_SAD_1 = process.env.PUBLIC_URL + "/aven sad1.jpeg";
const AVEN_SAD_2 = process.env.PUBLIC_URL + "/aven sad2.jpeg";

/* ── helpers ── */
const fireConfetti = () => {
  const colors = ["#ff6b9d", "#c44569", "#ffa07a", "#ff69b4", "#ff1493", "#ffd93d"];
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.65 }, colors });
  setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors }), 150);
  setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors }), 300);
};

const fireBigConfetti = () => {
  const end = Date.now() + 2500;
  const colors = ["#ff6b9d", "#c44569", "#ffa07a", "#ff69b4", "#ffd93d", "#25d366"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
};

const getRandomHint = () => AVEN_LIFELINE_HINTS[Math.floor(Math.random() * AVEN_LIFELINE_HINTS.length)];

/* ── TYPEWRITER COMPONENT ── */
const Typewriter = ({ text, speed = 50, onComplete }) => {
  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText((prev) => prev + text.charAt(i));
        i++;
      } else {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed, onComplete]);

  return <div className="vg-typewriter">{displayedText}</div>;
};

/* ═══════════════════════════════════════════════════════
   LOADING SCREEN
   ═══════════════════════════════════════════════════════ */
const LoadingScreen = ({ onDone }) => {
  const [progress, setProgress] = useState(0);
  const messages = [
    "Decrypting end-to-end encryption...",
    "Scanning 51,862 messages...",
    "Finding the funniest quotes...",
    "Waking up Baby Aven...",
    "Ready!",
  ];
  const msgIndex = Math.min(Math.floor(progress / 25), messages.length - 1);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(interval); setTimeout(onDone, 400); return 100; }
        return p + 2;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [onDone]);

  return (
    <div className="vg-loading">
      <div className="vg-loading-inner">
        <motion.div
          className="vg-loading-icon"
          animate={{ scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <MessageCircle size={56} strokeWidth={1.5} />
        </motion.div>

        <h1 className="vg-loading-title">The Agnihotri Archives</h1>
        <p className="vg-loading-subtitle">Loading Chat History...</p>

        <div className="vg-progress-track">
          <motion.div className="vg-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <motion.p className="vg-loading-msg" key={msgIndex}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        >
          {messages[msgIndex]}
        </motion.p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   GAME OVER / LOVE LETTER SCREEN
   ═══════════════════════════════════════════════════════ */
const GameOverScreen = ({ score, total, onRestart }) => {
  const pct = Math.round((score / total) * 100);
  const grade =
    pct >= 90 ? { emoji: "👑", label: "Agnihotri Legend!" } :
    pct >= 70 ? { emoji: "🌟", label: "You know us well!" } :
    pct >= 50 ? { emoji: "😊", label: "Not bad, try again!" } :
    { emoji: "😅", label: "Aven is disappointed..." };

  useEffect(() => { fireBigConfetti(); }, []);

  return (
    <div className="vg-gameover">
      <Link to="/" className="vg-back vg-back-light"><Home size={16} /> Home</Link>

      <motion.div className="vg-gameover-card"
        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 18 }}
      >
        <div className="vg-gameover-header">
          <motion.div className="vg-gameover-trophy"
            animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity }}
          >
            <Trophy size={48} />
          </motion.div>
          <h1>Game Over!</h1>
          <p className="vg-gameover-grade">
            <span role="img" aria-label="grade">{grade.emoji}</span> {grade.label}
          </p>
          <p className="vg-dates-unlocked">
            <span role="img" aria-label="dates">🥔</span> {score} Dates Collected!
          </p>
        </div>

        <div className="vg-score-ring">
          <svg viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,107,157,0.15)" strokeWidth="8" />
            <motion.circle cx="60" cy="60" r="52" fill="none" stroke="#ff6b9d" strokeWidth="8"
              strokeLinecap="round" strokeDasharray={2 * Math.PI * 52}
              initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
              animate={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - pct / 100) }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              style={{ transformOrigin: "center", transform: "rotate(-90deg)" }}
            />
          </svg>
          <div className="vg-score-ring-text">
            <span className="vg-score-num">{score}</span>
            <span className="vg-score-of">/ {total}</span>
          </div>
        </div>

        <motion.div className="vg-love-letter"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Heart size={20} fill="#ff6b9d" stroke="none" />
          <Typewriter text={FINAL_LOVE_LETTER} speed={30} />
        </motion.div>

        <div className="vg-gameover-photos">
          {AVEN_IMAGES.map((src, i) => (
            <motion.img key={i} src={src} alt="Family" className="vg-gameover-photo"
              initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.2 + i * 0.15 }}
            />
          ))}
        </div>

        <motion.button className="vg-btn-restart" onClick={onRestart}
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
        >
          <RotateCcw size={18} /> Play Again
        </motion.button>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN GAME
   ═══════════════════════════════════════════════════════ */
const ValentineGame = () => {
  const [phase, setPhase] = useState("loading"); // loading | playing | gameover
  const [qIndex, setQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [bestStreak, setBestStreak] = useState(0);
  const [lives, setLives] = useState(3);
  const [answered, setAnswered] = useState(false);
  const [chosenAnswer, setChosenAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [avenHint, setAvenHint] = useState(null);
  const [showAvenBtn, setShowAvenBtn] = useState(false);
  const [avenMood, setAvenMood] = useState("idle");
  const [showAvenReaction, setShowAvenReaction] = useState(false);
  const resultTimer = useRef(null);

  const q = GAME_QUESTIONS[qIndex];
  const cat = q ? CATEGORIES[q.category] : null;
  const isLast = qIndex === GAME_QUESTIONS.length - 1;
  const total = GAME_QUESTIONS.length;
  const progressPct = ((qIndex) / total) * 100;

  /* Aven twist: show hidden button after 2s */
  useEffect(() => {
    setShowAvenBtn(false);
    if (q && q.isAvenTwist) {
      const t = setTimeout(() => setShowAvenBtn(true), 2000);
      return () => clearTimeout(t);
    }
  }, [qIndex, q]);

  /* cleanup */
  useEffect(() => () => { if (resultTimer.current) clearTimeout(resultTimer.current); }, []);

  const handleAnswer = useCallback((ans) => {
    if (answered) return;
    setAnswered(true);
    setChosenAnswer(ans);

    const correct = q.answer === ans;
    
    // Show Aven reaction first
    setShowAvenReaction(true);
    setAvenMood(correct ? "happy" : "sad");
    
    if (correct) {
      fireConfetti();
    }
    
    // After Aven reacts, show result
    setTimeout(() => {
      setShowAvenReaction(false);
      if (correct) {
        setScore((s) => s + 1);
        setStreak((s) => { const n = s + 1; setBestStreak((b) => Math.max(b, n)); return n; });
      } else {
        setLives((l) => l - 1);
        setStreak(0);
      }
      setShowResult(true);
    }, 1800);
  }, [answered, q]);

  const handleNext = useCallback(() => {
    setShowResult(false);
    setShowAvenReaction(false);
    setAnswered(false);
    setChosenAnswer(null);
    setAvenHint(null);
    setAvenMood("idle");

    if (isLast || lives <= 0) {
      setPhase("gameover");
    } else {
      setQIndex((i) => i + 1);
    }
  }, [isLast, lives]);

  const handleRestart = useCallback(() => {
    setQIndex(0);
    setScore(0);
    setStreak(0);
    setLives(3);
    setAnswered(false);
    setChosenAnswer(null);
    setShowResult(false);
    setShowAvenReaction(false);
    setAvenHint(null);
    setAvenMood("idle");
    setPhase("playing");
  }, []);

  /* ── RENDER ── */
  if (phase === "loading") return <LoadingScreen onDone={() => setPhase("playing")} />;
  if (phase === "gameover") return <GameOverScreen score={score} total={total} onRestart={handleRestart} />;

  const isCorrect = chosenAnswer === q.answer;

  return (
    <div className="vg-game">
      {/* TOP BAR */}
      <div className="vg-topbar">
        <Link to="/" className="vg-back"><Home size={16} /> Home</Link>
        <div className="vg-lives">
          {[0, 1, 2].map((i) => (
            <motion.span key={i}
              animate={i >= lives ? { scale: 0.7, opacity: 0.25 } : { scale: [1, 1.2, 1] }}
              transition={i < lives ? { duration: 0.3 } : {}}
            >
              <Heart size={20} fill={i < lives ? "#ff6b9d" : "transparent"} stroke="#ff6b9d" />
            </motion.span>
          ))}
        </div>
        <div className="vg-streak">
          {streak > 0 && <Flame size={16} className="vg-flame" />}
          <span>{streak}</span>
        </div>
        <div className="vg-dates-collected">
          <span role="img" aria-label="dates">🥔</span> {score}
        </div>
      </div>

      {/* PROGRESS */}
      <div className="vg-progress">
        <div className="vg-progress-track-game">
          <motion.div className="vg-progress-fill-game"
            animate={{ width: `${progressPct}%` }} transition={{ duration: 0.4 }}
          />
        </div>
        <span className="vg-progress-label">{qIndex + 1} / {total}</span>
      </div>

      {/* CATEGORY BADGE */}
      <AnimatePresence mode="wait">
        <motion.div className="vg-category-badge" key={q.id}
          style={{ background: cat.color + "22", borderColor: cat.color }}
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
        >
          <span role="img" aria-label={cat.label}>{cat.icon}</span> {cat.label}
          {q.difficulty === "hard" && <Star size={12} className="vg-star" />}
        </motion.div>
      </AnimatePresence>

      {/* AVEN MASCOT */}
      <motion.div className="vg-aven"
        key={avenMood + qIndex}
        animate={
          avenMood === "happy" ? { scale: [1, 1.15, 1], y: [0, -12, 0] } :
          avenMood === "sad" ? { rotate: [0, -8, 8, -4, 0] } : {}
        }
        transition={{ duration: 0.5 }}
      >
        <img src={AVEN_IMAGES[qIndex % AVEN_IMAGES.length]} alt="Aven" className="vg-aven-img" />
        <span className="vg-aven-badge">
          {avenMood === "happy" ? "😄" : avenMood === "sad" ? "😢" : "👶"}
        </span>
      </motion.div>

      {/* CHAT BUBBLE */}
      <AnimatePresence mode="wait">
        <motion.div className="vg-chat-card" key={q.id}
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.97 }}
          transition={{ type: "spring", damping: 22 }}
        >
          {/* WhatsApp style header */}
          <div className="vg-wa-header">
            <div className="vg-wa-dot" />
            <span>Agnihotri Family Chat</span>
            <Sparkles size={14} />
          </div>

          {/* Quote bubble */}
          <div className="vg-wa-bubble">
            <p className="vg-wa-quote">{q.quote}</p>
            <span className="vg-wa-time">Who said this?</span>
          </div>

          {/* Aven Hint */}
          <AnimatePresence>
            {avenHint && (
              <motion.div className="vg-hint" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <Baby size={18} />
                <span>{avenHint}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Answer Buttons */}
          <div className="vg-answers">
            <motion.button
              className={`vg-btn vg-btn-dhruv ${answered && q.answer === "Dhruv" ? "vg-btn-correct" : ""} ${answered && chosenAnswer === "Dhruv" && !isCorrect ? "vg-btn-wrong" : ""}`}
              onClick={() => handleAnswer("Dhruv")}
              disabled={answered}
              whileHover={!answered ? { scale: 1.02 } : {}}
              whileTap={!answered ? { scale: 0.97 } : {}}
            >
              <span className="vg-btn-emoji" role="img" aria-label="man">👨‍💻</span> Dhruv
            </motion.button>

            <motion.button
              className={`vg-btn vg-btn-aastha ${answered && q.answer === "Aastha" ? "vg-btn-correct" : ""} ${answered && chosenAnswer === "Aastha" && !isCorrect ? "vg-btn-wrong" : ""}`}
              onClick={() => handleAnswer("Aastha")}
              disabled={answered}
              whileHover={!answered ? { scale: 1.02 } : {}}
              whileTap={!answered ? { scale: 0.97 } : {}}
            >
              <span className="vg-btn-emoji" role="img" aria-label="woman">👩‍🍳</span> Aastha
            </motion.button>

            <AnimatePresence>
              {showAvenBtn && (
                <motion.button
                  className={`vg-btn vg-btn-aven ${answered && q.answer === "Aven" ? "vg-btn-correct" : ""}`}
                  initial={{ opacity: 0, scale: 0, height: 0 }}
                  animate={{ opacity: 1, scale: 1, height: "auto" }}
                  exit={{ opacity: 0, scale: 0, height: 0 }}
                  onClick={() => handleAnswer("Aven")}
                  disabled={answered}
                  whileHover={!answered ? { scale: 1.02 } : {}}
                  whileTap={!answered ? { scale: 0.97 } : {}}
                >
                  <span className="vg-btn-emoji" role="img" aria-label="baby">👶</span> Aven
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Lifeline */}
          {!answered && (
            <button className="vg-lifeline" onClick={() => !avenHint && setAvenHint(getRandomHint())} disabled={!!avenHint}>
              <Baby size={16} /> Ask Aven for a Hint
            </button>
          )}
        </motion.div>
      </AnimatePresence>

      {/* AVEN REACTION OVERLAY */}
      <AnimatePresence>
        {showAvenReaction && (
          <motion.div className="vg-aven-reaction-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div className="vg-aven-reaction"
              initial={{ scale: 0, x: 100, y: 100 }}
              animate={{ 
                scale: 1, 
                x: 0, 
                y: 0,
                ...(avenMood === "happy" 
                  ? { y: [0, -15, 0] }
                  : { rotate: [0, -8, 8, -4, 4, 0] })
              }}
              transition={{ 
                scale: { type: "spring", damping: 12 },
                y: avenMood === "happy" ? { duration: 0.6, repeat: 2 } : {},
                rotate: avenMood === "sad" ? { duration: 0.5 } : {}
              }}
            >
              <img 
                src={avenMood === "happy" ? AVEN_HAPPY : (Math.random() > 0.5 ? AVEN_SAD_1 : AVEN_SAD_2)}
                alt="Aven reacts"
                className="vg-aven-reaction-img"
              />
              <div className="vg-aven-speech">
                {avenMood === "happy" ? "Yay! Good job Mama!" : "Haww... that's not right!"}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* RESULT OVERLAY */}
      <AnimatePresence>
        {showResult && (
          <motion.div className={`vg-result-overlay ${isCorrect ? "vg-result-correct" : "vg-result-wrong"}`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div className="vg-result-card"
              initial={{ scale: 0.7, y: 30 }} animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", damping: 16 }}
            >
              <span className="vg-result-icon" role="img" aria-label="result">
                {isCorrect ? "🎉" : "💔"}
              </span>
              <h2>{isCorrect ? "Correct!" : "Oops!"}</h2>
              <p className="vg-result-detail">
                {isCorrect
                  ? streak > 1 ? `${streak} in a row! Aven is bouncing!` : "Aven approves!"
                  : `It was ${q.answer}!`}
              </p>
              {!isCorrect && lives <= 1 && (
                <p className="vg-result-gameover-hint">Last life lost!</p>
              )}
              <motion.button className="vg-btn-next" onClick={handleNext}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              >
                {(isLast || (!isCorrect && lives <= 1)) ? "See Results" : "Next"} <ArrowRight size={18} />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ValentineGame;
